import type { PaymentHistory } from "pages/liens/LienTypes";
import type { PaginatedResponse } from "types/DataTypes";

import { api } from "./api";

export const paymentHistoriesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPaymentHistories: build.query<
      PaginatedResponse<PaymentHistory>,
      {
        lienId: string;
        isDisputed?: boolean;
        fromDate?: string;
        toDate?: string;
        limit?: number;
        offset?: number;
        levelOfAppeal?: string;
      }
    >({
      query: ({
        lienId,
        isDisputed,
        fromDate,
        toDate,
        limit,
        offset,
        levelOfAppeal,
      }) => {
        const params = new URLSearchParams();
        params.append("lien", lienId);
        if (isDisputed !== undefined) {
          params.append("is_disputed", isDisputed.toString());
        }
        if (fromDate) {
          params.append("from_date", fromDate);
        }
        if (toDate) {
          params.append("to_date", toDate);
        }
        if (limit !== undefined) {
          params.append("limit", limit.toString());
        }
        if (offset !== undefined) {
          params.append("offset", offset.toString());
        }
        if (levelOfAppeal !== undefined) {
          params.append("level_of_appeal", levelOfAppeal.toString());
        }

        return {
          url: `/api/v1/payment-histories/?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["PaymentHistories"],
    }),
    createPaymentHistory: build.mutation<
      PaymentHistory,
      {
        data: Partial<PaymentHistory>;
      }
    >({
      query: ({ data }) => ({
        url: "/api/v1/payment-histories/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PaymentHistories", "LienFile", "LienValidation"],
    }),
    updatePaymentHistory: build.mutation<
      PaymentHistory,
      {
        id: number;
        data: Partial<PaymentHistory>;
      }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/payment-histories/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["PaymentHistories", "LienFile", "LienValidation"],
    }),
    deletePaymentHistory: build.mutation<
      void,
      {
        id: number;
      }
    >({
      query: ({ id }) => ({
        url: `/api/v1/payment-histories/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["PaymentHistories", "LienFile", "LienValidation"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPaymentHistoriesQuery,
  useCreatePaymentHistoryMutation,
  useUpdatePaymentHistoryMutation,
  useDeletePaymentHistoryMutation,
} = paymentHistoriesApi;
